// ── Billing page ──
// Presentational. Every string here is customer-facing and follows the plain-English
// rule: say what is true about the reader's money in the words they would use.
import React, { useEffect, useRef, useState } from 'react';
import AppHeader from '../shared/AppHeader.jsx';
import PlanPicker from './PlanPicker.jsx';
import { INR, fmtDate, mandateLine, triggerLine, offerLine, paymentStatusLabel, paymentReason } from './billing.api.js';
import { INR as INR2 } from './plans.api.js';
import BrandLoader from '../shared/BrandLoader.jsx';

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '26px 30px' };
const h2 = { fontSize: 19, fontWeight: 750, margin: '0 0 8px' };
const sub = { fontSize: 15, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 18px', maxWidth: '62ch' };
const fieldStyle = {
  width: '100%', padding: '14px 16px', borderRadius: 10, border: '1px solid var(--border)',
  background: 'var(--surface2)', color: 'var(--text)', fontSize: 16, outline: 'none', fontFamily: 'inherit',
};
const primaryBtn = {
  padding: '12px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: 'var(--accent-ink)',
  fontSize: 15.5, fontWeight: 750, cursor: 'pointer', fontFamily: 'inherit',
};
const ghostBtn = { ...primaryBtn, background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)', fontWeight: 600 };
const dangerBtn = { ...ghostBtn, color: 'var(--loss)', borderColor: 'color-mix(in oklab, var(--loss) 45%, var(--border))' };
const label = { display: 'block', fontSize: 14.5, color: 'var(--muted)', marginBottom: 9 };
const mono = { fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: 'tabular-nums' };

function Pill({ tone, children }) {
  const c = { on: 'var(--profit)', off: 'var(--muted)', warn: 'var(--warn)', bad: 'var(--loss)' }[tone] || 'var(--muted)';
  return <span style={{ display: 'inline-block', padding: '4px 11px', borderRadius: 99, fontSize: 13, fontWeight: 700, ...mono,
    color: c, background: `color-mix(in oklab, ${c} 16%, transparent)` }}>{children}</span>;
}

// ── states of the mandate card ─────────────────────────────────────────────


function SetupCard({ status, states, busy, onSetup, defaultPhone }) {
  const [phone, setPhone] = useState(defaultPhone || '');
  useEffect(() => { if (defaultPhone) setPhone((p) => p || defaultPhone); }, [defaultPhone]);
  const [name, setName] = useState('');
  const [billingState, setBillingState] = useState('');
  const digits = phone.replace(/\D/g, '');
  // The guardrail offer buys a plan later, with nobody here to ask, so the state is asked now.
  // Never greyed out (CLAUDE.md, front-end standard 7): pressing says what is missing, outlines it and focuses it.
  const [missing, setMissing] = useState(null);   // null | 'phone' | 'state'
  const phoneRef = useRef(null), stateRef = useRef(null);
  const setup = (method) => {
    if (digits.length !== 10) { setMissing('phone'); if (phoneRef.current) phoneRef.current.focus(); return; }
    if (status.offer && !billingState) { setMissing('state'); if (stateRef.current) stateRef.current.focus(); return; }
    setMissing(null);
    onSetup({ phone: digits, name, method, billingState: billingState || undefined });
  };
  const bad = (k) => (missing === k ? { ...fieldStyle, border: '1px solid var(--loss)' } : fieldStyle);
  return <div style={card}>
    <h2 style={h2}>Set up how you pay</h2>
    <p style={sub}>
      You give your bank permission once, for up to <b style={{ color: 'var(--text)' }}>{INR(status.mandate ? status.mandate.maxAmount : 15000)}</b> a month, using UPI Autopay or a card.
      We charge nothing today — ₹1 to confirm the account, refunded straight away.
    </p>
    <p style={{ ...sub, color: 'var(--text)', fontWeight: 600 }}>{offerLine(status.trigger, status.offer)}</p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }} className="bl-grid">
      <div>
        <label style={label}>Mobile number</label>
        <input ref={phoneRef} aria-invalid={missing === 'phone' || undefined} aria-describedby="setup-missing" inputMode="numeric" placeholder="10 digits" value={phone}
          onChange={(e) => { setPhone(e.target.value); if (e.target.value.replace(/\D/g, '').length === 10 && missing === 'phone') setMissing(null); }} style={bad('phone')} />
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>Your bank sends the approval request here.</div>
      </div>
      <div>
        <label style={label}>Name on the account <span style={{ opacity: .7 }}>(optional)</span></label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
      </div>
      {status.offer && <div>
        <label style={label}>Where you are billed</label>
        <select ref={stateRef} aria-invalid={missing === 'state' || undefined} aria-describedby="setup-missing" value={billingState}
          onChange={(e) => { setBillingState(e.target.value); if (e.target.value && missing === 'state') setMissing(null); }} style={bad('state')}>
          <option value="">Choose your state</option>
          {Object.entries(states || {}).sort((a, b) => String(a[1]).localeCompare(String(b[1])))
            .map(([code, n]) => <option key={code} value={code}>{n}</option>)}
        </select>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>For the tax on your invoice. The price is the same either way.</div>
      </div>}
    </div>
    {missing && <p id="setup-missing" role="alert" style={{ color: 'var(--loss)', fontSize: 14, margin: '18px 0 0' }}>
      {missing === 'phone' ? 'Enter your 10-digit mobile number: your bank sends the approval request there.' : 'Choose the state you are billed in.'}</p>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 22, flexWrap: 'wrap' }}>
      {status.provider === 'razorpay' ? (<>
        {/* Razorpay fixes the instrument on the order, so the choice is made here. */}
        <button onClick={() => setup('upi')} disabled={busy} style={{ ...primaryBtn, opacity: busy ? .6 : 1 }}>
          {busy ? 'Opening…' : 'Set up with UPI Autopay'}
        </button>
        <button onClick={() => setup('card')} disabled={busy} style={{ ...ghostBtn, opacity: busy ? .6 : 1 }}>
          Set up with a card
        </button>
      </>) : (
        <button onClick={() => setup('upi')} disabled={busy} style={{ ...primaryBtn, opacity: busy ? .6 : 1 }}>
          {busy ? 'Opening your bank…' : 'Set up with UPI or card'}
        </button>
      )}
      <span style={{ fontSize: 13.5, color: 'var(--muted)' }}>You approve it on your bank's or UPI app's own page. We never see your card or UPI details.</span>
    </div>
  </div>;
}

function PendingCard({ mandate, busy, onContinue, onCheck }) {
  return <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}><h2 style={{ ...h2, margin: 0 }}>Almost there</h2><Pill tone="warn">Waiting for approval</Pill></div>
    <p style={sub}>{mandateLine(mandate)}</p>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {mandate.checkout && <button onClick={() => onContinue(mandate)} disabled={busy} style={primaryBtn}>Continue setup</button>}
      <button onClick={onCheck} disabled={busy} style={ghostBtn}>{busy ? 'Checking…' : 'Check again'}</button>
    </div>
  </div>;
}

function ActiveCard({ status, busy, onCancel }) {
  const m = status.mandate;
  const paying = m.billingActive;
  const trial = !paying && m.trialEndsOn;
  return <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
      <h2 style={{ ...h2, margin: 0 }}>{paying ? `You pay ${INR(status.priceInr)} a month`
        : trial ? `Your free ${status.offer ? status.offer.planName : ''} trial is on` : 'Your mandate is set up'}</h2>
      <Pill tone={paying || trial ? 'on' : 'off'}>{paying ? 'Billing on' : trial ? 'Free trial' : 'Not charging'}</Pill>
    </div>
    {paying ? (
      <p style={sub}>
        Billing {triggerLine(m.triggerReason)} on {fmtDate(m.activatedAt)}. We raise your next charge on <b style={{ color: 'var(--text)' }}>{fmtDate(m.nextChargeOn)}</b>,
        and the money leaves your account two days after that. Your bank tells you before it goes.
      </p>
    ) : (
      m.trialEndsOn ? (
        <p style={sub}>
          Your free {status.offer ? status.offer.planName : ''} trial runs until <b style={{ color: 'var(--text)' }}>{fmtDate(m.trialEndsOn)}</b>.
          {status.offer && status.offer.priceInr != null ? <> Then {INR2(status.offer.priceInr)} {status.offer.product === 'annual' ? 'a year' : 'a month'} from this autopay.</> : null}
          {' '}Cancel before it ends and nothing is taken — you keep the trial either way.
        </p>
      ) : <p style={sub}>{offerLine(status.trigger, status.offer)} Your bank approved the mandate on {fmtDate(m.authorizedAt)}.</p>
    )}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, margin: '6px 0 20px' }} className="bl-grid3">
      <Fact k="Pays with">{m.paymentMethod ? m.paymentMethod.toUpperCase() : '—'}</Fact>
      <Fact k="Up to, per month">{INR(m.maxAmount)}</Fact>
      <Fact k="Price">{status.offer ? `${INR2(status.offer.priceInr)} / ${status.offer.product === 'annual' ? 'year' : 'month'}`
        : status.priceInr ? INR(status.priceInr) + ' / month' : '—'}</Fact>
    </div>
    <button onClick={onCancel} disabled={busy} style={dangerBtn}>Cancel mandate</button>
    <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 12 }}>Cancelling stops every future charge. A charge already scheduled with your bank still goes through.</div>
  </div>;
}

// A guardrail stopped them and bought the offer's plan; the money has not landed yet.
function FirstPaymentCard({ status, busy, onCancel }) {
  const fp = status.mandate.firstPayment;
  const plan = status.offer && status.offer.planKey === fp.planKey ? status.offer.planName : fp.planKey;
  return <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
      <h2 style={{ ...h2, margin: 0 }}>Your {plan} plan is starting</h2>
      <Pill tone="warn">First payment on its way</Pill>
    </div>
    <p style={sub}>
      {status.mandate.triggerReason === 'trial_ended' ? 'Your free trial is ending' : 'One of your rules stopped you'}, so the free part is over. {INR2(fp.amount)} {fp.product === 'annual' ? 'a year' : 'a month'} will be taken from
      your autopay{fp.on ? <> on or after <b style={{ color: 'var(--text)' }}>{fmtDate(fp.on)}</b></> : ' in the next few days'}. Your bank tells you
      before it goes, and your plan starts the day it lands.
    </p>
    <button onClick={onCancel} disabled={busy} style={dangerBtn}>Cancel autopay</button>
    <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 12 }}>Don't want to go on? Cancel before that date.</div>
  </div>;
}

function Fact({ k, children }) {
  return <div style={{ padding: '12px 14px', background: 'var(--surface2)', borderRadius: 10 }}>
    <div style={{ fontSize: 12, color: 'var(--muted)', letterSpacing: '.04em', textTransform: 'uppercase', ...mono }}>{k}</div>
    <div style={{ fontSize: 16.5, fontWeight: 700, marginTop: 4 }}>{children}</div>
  </div>;
}

function EndedCard({ mandate, busy, onSetupAgain }) {
  return <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}><h2 style={{ ...h2, margin: 0 }}>No active mandate</h2><Pill tone="bad">{mandate.status === 'CANCELLED' ? 'Cancelled' : mandate.status}</Pill></div>
    <p style={sub}>{mandateLine(mandate)}</p>
    <button onClick={onSetupAgain} disabled={busy} style={primaryBtn}>Set up a new one</button>
  </div>;
}

function PaymentsCard({ payments }) {
  return <div style={card}>
    <h2 style={h2}>Your charges</h2>
    {payments.length === 0 ? (
      <div style={{ padding: '14px 0 4px', color: 'var(--muted)', fontSize: 14.5 }}>Nothing has been charged.</div>
    ) : payments.map((p) => (
      <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '15px 0', borderTop: '1px solid var(--border)' }}>
        <div>
          <div style={{ fontSize: 15.5, fontWeight: 600 }}>{paymentReason(p)}</div>
          <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 3 }}>{p.scheduleDate ? `Scheduled for ${fmtDate(p.scheduleDate)}` : fmtDate(p.createdAt)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 16, fontWeight: 700, ...mono }}>{INR(p.amount)}</span>
          <Pill tone={{ SUCCESS: 'on', FAILED: 'bad', CANCELLED: 'off', PENDING: 'warn' }[p.status] || 'warn'}>{paymentStatusLabel(p)}</Pill>
        </div>
      </div>
    ))}
  </div>;
}

// ── page ───────────────────────────────────────────────────────────────────

export default function BillingPage({ user, status, busy, defaultPhone, plans, features, states, selling, paymentsPaused, subscription, quote,
  onQuote, onPay, onClearQuote, onSetup, onContinue, onCheck, onCancel, onSetupAction, onDowngrade, onCancelDowngrade, preselect, planOn }) {
  const [setupAgain, setSetupAgain] = useState(false);
  let body;
  if (!status) body = <div style={card}><BrandLoader inline /></div>;
  // THE LEGACY PATH BEING OFF IS NOT NEWS FOR THIS PAGE. `status.enabled` is the old
  // one-price-for-everyone mandate flow, retired when BILLING_PRICE_INR went to 0. It used to
  // render "Payments are not switched on yet" here — above a plan picker that was selling
  // plans perfectly well, to somebody who already had a subscription. Whether a plan can be
  // bought is `selling`, from /api/plans, and PlanPicker says so itself.
  else if (!status.enabled) body = null;
  // Already on a plan: Membership is where their autopay lives; this card has nothing to offer them.
  else if (status.subscribed) body = null;
  else {
    const m = status.mandate;
    const pending = m && (m.status === 'INITIALIZED' || m.status === 'BANK_APPROVAL_PENDING');
    if (!m || setupAgain) body = <SetupCard status={status} states={states} busy={busy} onSetup={onSetup} defaultPhone={defaultPhone} />;
    else if (pending) body = <PendingCard mandate={m} busy={busy} onContinue={onContinue} onCheck={onCheck} />;
    else if (m.firstPayment && m.status === 'ACTIVE') body = <FirstPaymentCard status={status} busy={busy} onCancel={onCancel} />;
    else if (m.status === 'ACTIVE') body = <ActiveCard status={status} busy={busy} onCancel={onCancel} />;
    else body = <EndedCard mandate={m} busy={busy} onSetupAgain={() => setSetupAgain(true)} />;
  }
  return (
    <div style={{ fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      <AppHeader user={user} active="Membership" back={{ href: '/trading-dashboard', label: 'Dashboard' }} />
      <div style={{ padding: '30px 24px 60px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <h1 style={{ fontSize: 30, fontWeight: 750, margin: 0, letterSpacing: '-0.02em' }}>Billing</h1>
        {/* Just paid, and no broker yet: the plan does nothing until a broker account is connected. */}
        {planOn && (
          <section style={card} role="status">
            <h2 style={h2}>{planOn} is on. Next: connect your broker</h2>
            <p style={sub}>Your rules start working once Zensibly can see your broker account.</p>
            <a href="/brokers" style={{ display: 'inline-block', padding: '12px 22px', borderRadius: 10, background: 'var(--accent)',
              color: 'var(--accent-ink)', fontSize: 15.5, fontWeight: 750, textDecoration: 'none' }}>Connect your broker</a>
          </section>
        )}
        {body}
        {/* Collection paused on /admin-payments: nothing can be bought or renewed, and nobody loses their plan for it. */}
        {paymentsPaused ? (
          <section style={card} role="status">
            <h2 style={h2}>Payments are paused right now</h2>
            <p style={{ ...sub, margin: 0 }}>{paymentsPaused}</p>
          </section>
        ) : (
          <PlanPicker plans={plans} features={features} states={states} selling={selling} subscription={subscription} quote={quote}
            busy={busy} onQuote={onQuote} onPay={onPay} onClearQuote={onClearQuote} defaultPhone={defaultPhone}
            onDowngrade={onDowngrade} onCancelDowngrade={onCancelDowngrade} onSetupAction={onSetupAction} preselect={preselect} />
        )}
        {status && status.enabled && <PaymentsCard payments={status.payments || []} />}
        {status && status.enabled && status.mode === 'sandbox' && (
          <div style={{ fontSize: 13, color: 'var(--warn)', ...mono }}>Test mode — no real money moves.</div>
        )}
      </div>
    </div>
  );
}
